import { DocumentsImagesStoreAbstract } from '@shared/documents-images/DocumentsImagesStoreAbstract';
import { DocumentsImagesFilter } from '@shared/documents-images/ui/DocumentsImagesFilter';
import { DocumentsImagesPanelThumbnails } from '@shared/documents-images/ui/DocumentsImagesPanelThumbnails';
import { DocumentsPanelToolbar } from '@shared/documents-images/ui/DocumentsPanelToolbar';
import { ImagesPanelToolbar } from '@shared/documents-images/ui/ImagesPanelToolbar';
import { MapPanelFilter } from '@shared/map/MapPanelFilter';
import { TreeSelectionFeedback } from '@shared/map/TreeSelectionFeedback';
import { FileTreeService } from '@shared/services/FileTreeService';
import { childFilterPresets, Tree, TreeNode } from '@shared/ui-native/Tree';
import { TabPane, Tabs } from '@shared/ui-react/Tabs';
import { PageStateUtils } from '@shared/user-state/PageStateUtils';
import { cs } from '@shared/utils/utils-classname';
import { Tree as TreeDS, type TreeNodeData, collectAllFolderIds } from 'design-system';
import { action, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import './DocumentsImagesPanelGenericComponent.scss';
import { PdfTemplateFill } from '@shared/documents/ui/PdfTemplateFill';
import { Events } from '@shared/Events';
import { IContextMenuItem, renderContextMenuAtPosition } from '@shared/ui-react/ContextMenu';
import { TreeToolbar } from '@shared/ui-react/TreeToolbar';

@observer
export class DocumentsImagesPanelGenericComponent
    extends React.Component<{
        type: 'images' | 'documents';
        store: DocumentsImagesStoreAbstract<any>;
        treeData: ITreeData | null;
        storageKey: string;
        onOpenItem: (id: number) => void;
        onEditItem?: (id: number) => void;
        onEditItems?: (items: DocumentTypes[], activateEditLocation?: boolean) => void;
        onGoToFile?: (uuid: string) => void;
        pinLeftOffset?: number;
        mobileView?: boolean;
        toggleExpandPanel?: () => void;
        onBulkEdit?: () => void;
        onOpenUploader?: () => void;
        onCreateExternalDocument?: () => void;
        showFillTab?: boolean;
        onOpenFillTemplate?: () => void;
        getRenamedName?: (template: string, description: string, folderUuid: string, latLon: [number, number], doc: IDocument) => string;
        getUserLatLon?: () => [number, number];
        hideToolbar?: boolean;
    }>
    implements IStateful<IMapPageStateDocumentsImagesPanel>
{
    public tree: Tree = null;
    private toolbarEl: HTMLDivElement = null;

    @observable activeTab = 0;
    @observable isRendered = false;
    @observable isTwoColumns = false;

    @observable private checkedFolderIds: (number | string)[] = [];
    @observable private selectedFolderId: number | string | null = null;
    @observable private treeNodes: TreeNodeData[] = [];
    @observable private filterText = '';
    @observable private expandedIds: (number | string)[] = [];
    @observable private hideEmptyFolders = true;
    @observable private expandOnStart = 'firstLevel';

    constructor(props) {
        super(props);
        makeObservable(this);
    }

    componentDidMount() {
        if (this.props.treeData) {
            this.initTree();
        }

        setTimeout(() => {
            try {
                const ro = new ResizeObserver((entries, observer) => {
                    this.isTwoColumns = this.props.mobileView ? false : entries[0].contentRect.width > 550;
                });
                ro.observe(this.toolbarEl?.parentNode as HTMLDivElement);
            } catch {}
        }, 1000);
    }

    componentDidUpdate(prevProps: any) {
        if (!this.tree && prevProps.treeData !== this.props.treeData) {
            this.initTree();
        }
    }

    render() {
        const store = this.props.store;
        const noItems = store.hasLoadedItems && store.items.length === 0;

        if (!this.props.treeData) return <div className='overlay-loading no-bg' style={{ height: '100%' }}></div>;

        let Thumbnails = null;
        if (this.props.store.thumbnails) {
            const folderNameStr = FileTreeService.findNodeById(this.props.treeData, this.props.store.showThumbnailsForFolderId)?.name || '';
            const canDownload = kuura.hasPrivilege(this.props.type === 'images' ? 'Download_Image' : 'Download_Model');

            Thumbnails = (
                <DocumentsImagesPanelThumbnails
                    type={this.props.type}
                    items={this.props.store.thumbnails?.filtered}
                    unfilteredItems={this.props.store.thumbnails?.unfiltered}
                    focusItemId={this.props.store.focusThumbnailId}
                    folderName={folderNameStr}
                    canEdit={this.props.mobileView ? false : this.props.type === 'images' ? kuura.hasPrivilege('IMAGE_PAGE') : true}
                    onEditSelected={(items, activateEditLocation) => this.props.onEditItems(items, activateEditLocation)}
                    onEdit={this.props.onEditItem}
                    onClick={this.props.onOpenItem}
                    onDelete={item => this.props.store.removeItem(item)}
                    onClose={this.closeThumbnailsPanel}
                    onExpand={this.props.mobileView ? null : this.props.toggleExpandPanel}
                    canDownload={canDownload && !this.props.mobileView}
                    onGoToFile={this.props.onGoToFile}
                />
            );
        }

        const { allFolderCounts, filteredFolderCounts } = store.counts;
        return (
            <>
                <div className='sidebar-toolbar' ref={el => (this.toolbarEl = el)}>
                    {this.props.hideToolbar ? null : this.props.type === 'documents' ? (
                        <DocumentsPanelToolbar store={this.props.store} openCreateExternal={this.props.onCreateExternalDocument} openFillTemplate={this.props.onOpenFillTemplate} />
                    ) : (
                        <ImagesPanelToolbar store={this.props.store} onBulkEdit={this.props.onBulkEdit} onOpenUploader={this.props.onOpenUploader} />
                    )}
                </div>

                <div className={cs('doc-images-panel__wrapper', { 'two-col': this.isTwoColumns })}>
                    <div className='flex-column of-h' style={{ flex: this.props.mobileView ? '1 1 100%' : '1 1 50%' }}>
                        <Tabs displayValue='flex' flexDirection='column' index={this.props.mobileView ? this.activeTab : 0} onChange={i => (this.activeTab = i)}>
                            <TabPane title={__('general.folder_tree')}>
                                <div className='flex-column of-h' style={{ margin: '0 auto', width: '100%', maxWidth: 'auto' }}>
                                    <div className='flex-row gap-half ai-center'>
                                        {this.isRendered && !noItems && (
                                            <div className='tree-toolbar-wrapper centered' style={{ flexGrow: 1, overflow: 'hidden' }}>
                                                <TreeToolbar
                                                    filterText={this.filterText}
                                                    onFilterChange={text => {
                                                        this.filterText = text;
                                                    }}
                                                    onExpandAll={() => {
                                                        this.expandedIds = Array.from(collectAllFolderIds(this.treeNodes));
                                                    }}
                                                    onCollapseAll={() => {
                                                        this.expandedIds = [];
                                                    }}
                                                    hideEmptyFolders={!this.hideEmptyFolders}
                                                    hideEmptyFoldersAlways={this.props.type === 'documents' || this.props.mobileView}
                                                    onToggleHideEmptyFolders={() => {
                                                        this.hideEmptyFolders = !this.hideEmptyFolders;
                                                        this.tree?.toggleHideEmptyFolders();
                                                        setTimeout(() => this.refreshNodes(), 50);
                                                    }}
                                                    expandOnStart={this.expandOnStart}
                                                    onSetExpandOnStart={value => {
                                                        this.expandOnStart = value;
                                                        this.tree?.setExpandOnStart(value as any);
                                                    }}
                                                    showContextMenuCreateFolder={this.props.type !== 'documents'}
                                                    onOpenCreateFolder={() => {
                                                        this.tree?.openCreateFolder(this.tree.getSelected() ?? this.tree.root);
                                                    }}
                                                    hasContentCounts={true}
                                                />
                                            </div>
                                        )}
                                        {this.isRendered && !noItems && (
                                            <MapPanelFilter
                                                pinned={this.props.mobileView ? false : store.filterPinned}
                                                pinLeftOffset={this.props.pinLeftOffset}
                                                opened={store.filterOpened}
                                                activeFiltersCount={store.numberOfAppliedFilters}
                                                clearFilter={store.clearFilter}
                                                togglePinned={
                                                    this.props.mobileView
                                                        ? null
                                                        : () => {
                                                              store.filterPinned = !store.filterPinned;
                                                              store.filterOpened = true;
                                                          }
                                                }
                                                toggleOpened={() => (store.filterOpened = !store.filterOpened)}
                                            >
                                                <DocumentsImagesFilter store={store} treeData={this.props.treeData} type={this.props.type} mobile={false} />
                                            </MapPanelFilter>
                                        )}
                                    </div>

                                    <div className={cs({ 'fade-disable': !store.hasLoadedItems })} style={{ display: noItems ? 'none' : 'block' }}>
                                        <TreeSelectionFeedback
                                            checkedCount={store.filter.folderIds?.length ?? 0}
                                            loadedItemsCount={store.itemsFiltered.length}
                                            totalItemsCount={store.items.length}
                                            onClearSelection={this.clearSelection}
                                            onSelectAll={this.selectAll}
                                            countTooltip={__('js_map.images.selected_count_tooltip')}
                                        />
                                    </div>

                                    <div
                                        className={cs({ 'fade-disable': !store.hasLoadedItems })}
                                        style={{ display: noItems ? 'none' : 'flex', flexDirection: 'column', overflow: 'hidden', flexGrow: 1 }}
                                    >
                                        <div style={{ flex: '1 1 0', overflow: 'hidden', minHeight: 100 }}>
                                            {this.treeNodes.length > 0 && (
                                                <TreeDS
                                                    nodes={this.treeNodes}
                                                    showRoot={this.treeNodes.length === 1}
                                                    checkable={true}
                                                    checkedIds={this.checkedFolderIds}
                                                    selectedId={this.selectedFolderId}
                                                    expandedIds={this.expandedIds}
                                                    onExpandedIdsChange={ids => {
                                                        this.expandedIds = ids;
                                                    }}
                                                    expandOnStart='firstLevel'
                                                    selectOnNavigate={this.props.mobileView}
                                                    hideEmptyFolders={!this.hideEmptyFolders}
                                                    sortChildren={true}
                                                    propagateCheckbox={false}
                                                    filterText={this.filterText}
                                                    renderLabel={node => {
                                                        const total = allFolderCounts?.[node.id as number];
                                                        const filtered = filteredFolderCounts?.[node.id as number];
                                                        if (!(total > 0)) return node.name;
                                                        const countText = filtered != null && filtered !== total ? `(${filtered}/${total})` : `(${total})`;
                                                        return (
                                                            <>
                                                                {node.name} <span style={{ color: 'var(--ik-color-text-weakest)' }}>{countText}</span>
                                                            </>
                                                        );
                                                    }}
                                                    onNodeSelected={this.handleFolderSelected}
                                                    onNodeChecked={this.handleFolderChecked}
                                                    onNodeUnchecked={this.handleFolderUnchecked}
                                                    onContextMenu={this.handleContextMenu}
                                                    treeData={undefined}
                                                    onDoubleClick={undefined}
                                                    loadingIds={undefined}
                                                    className={undefined}
                                                />
                                            )}
                                        </div>
                                        {this.props.mobileView && Thumbnails && (
                                            <div
                                                className='doc-images-panel__mobile-thumbnails'
                                                style={{ flex: '0 0 auto', borderTop: '2px solid #ececec', maxHeight: '50vh', overflowY: 'auto' }}
                                            >
                                                {Thumbnails}
                                            </div>
                                        )}
                                    </div>

                                    {noItems && (
                                        <div className='sidebar-section-body-empty'>
                                            <div className='panel-no-items flex-column gap-half'>
                                                <i className={this.props.type === 'images' ? 'iki iki-photos' : 'iki iki-files'}></i>
                                                {this.props.type === 'images' ? __('js_images.no_images') : __('js_map.no_documents')}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </TabPane>

                            {this.props.showFillTab && this.props.type === 'documents' && (
                                <TabPane title={__('js_files.fill_template')}>
                                    <PdfTemplateFill
                                        getRenamedName={this.props.getRenamedName}
                                        getUserLatLon={this.props.getUserLatLon}
                                        onSaved={() => this.props.store.fetchAndSet()}
                                    />
                                </TabPane>
                            )}
                        </Tabs>
                    </div>

                    {!this.props.mobileView && this.props.store.thumbnails && (
                        <div
                            className={cs('second-col', 'flex-column', 'of-h', { 'overlay-loading': this.props.store.isLoadingItems })}
                            style={{ flex: '1 1 50%', borderTop: '1px solid #ccc', paddingTop: 10, minHeight: 140 }}
                        >
                            {Thumbnails}
                        </div>
                    )}
                </div>
            </>
        );
    }

    private handleContextMenu = (node: TreeNodeData, position: { x: number; y: number }) => {
        const items: IContextMenuItem[] = [];
        const nativeNode = node.data as TreeNode;
        const isMobilePage = window.location.href.includes('mobile');

        if (node.type === 'folder' && this.props.type !== 'documents' && !isMobilePage && !nativeNode.state?.readOnly) {
            items.push({
                label: __('js_files.create_folder'),
                icon: 'iki iki-folder-closed',
                action: () => this.tree.openCreateFolder(nativeNode),
            });
        }

        if (!items.length) return;

        Events.trigger('map-hide-overlays');
        renderContextMenuAtPosition(items, position.x, position.y, 'tree-context-menu', () => {});
    };

    private convertNode = (node: TreeNode): TreeNodeData => ({
        id: node.id,
        name: node.ajaxData?.name ?? node.text ?? '',
        type: 'folder',
        children: node.children?.filter(c => c.isFolder() && !c.state.hidden).map(this.convertNode),
        data: node,
    });

    @action private refreshNodes(): void {
        if (!this.tree) return;
        this.treeNodes = this.tree.children.filter(n => !n.state.hidden).map(this.convertNode);
    }

    private initTree() {
        if (this.tree) return;

        this.tree = new Tree({
            renderView: false,
            showRoot: true,
            showCheckboxes: true,
            propagateCheckbox: false,
            selectionCheckbox: false,
            checkOnHighlightSelected: true,
            showContextMenuCreateFolder: this.props.type !== 'documents',
            childFilter: childFilterPresets.onlyFolders,
            hideEmptyFolders: true,
            hideEmptyFoldersAlways: this.props.type === 'documents' || this.props.mobileView,
            storageKey: this.props.storageKey,
            fetchContentCountsFn: this.props.store.getFolderCounts,
        });

        // sync checked state to MobX store for design system Tree + apply filter
        this.tree.on(
            'check',
            action(() => {
                this.checkedFolderIds = this.tree.getChecked().map(n => n.id);
                this.applyFileTreeFilter();
            })
        );

        this.tree.on('render', () => {
            setTimeout(() => {
                action(() => {
                    this.isRendered = true;
                    this.refreshNodes();
                })();

                if (this.stateful.queuedRestoreState) {
                    this.stateful.restoreState(this.stateful.queuedRestoreState);
                    this.stateful.queuedRestoreState = null;
                }
            });
        });

        this.tree.initAndRender(this.props.treeData);

        // Sync persisted settings from native tree to MobX store
        const storedState = this.tree.getStoredState();
        if (storedState?.expandOnStart) {
            this.expandOnStart = storedState.expandOnStart;
        }
        if (storedState?.hideEmptyFolders != null) {
            this.hideEmptyFolders = storedState.hideEmptyFolders;
        }
        const expandedDsIds: (number | string)[] = [];
        this.tree.each(c => {
            if (c.isFolder() && c.state.expanded) {
                expandedDsIds.push(c.id);
            }
        }, true);
        this.expandedIds = expandedDsIds;
    }

    private handleFolderSelected = (node: TreeNodeData): void => {
        action(() => {
            this.selectedFolderId = node.id;
        })();
        this.openThumbnailsPanel(node.id as number);
    };

    private handleFolderChecked = (node: TreeNodeData): void => {
        this.tree?.getById(Number(node.id))?.setChecked(1);
    };

    private handleFolderUnchecked = (node: TreeNodeData): void => {
        this.tree?.getById(Number(node.id))?.setChecked(0);
    };

    selectAll = (): void => {
        if (!this.tree) return;
        this.tree.setChecked(1, true, 'deep');
        action(() => {
            this.checkedFolderIds = this.tree.getChecked().map(n => n.id);
        })();
        this.applyFileTreeFilter();
    };

    clearSelection = (): void => {
        if (!this.tree) return;
        this.props.store.filter.folderIds = [];
        this.tree.setChecked(0, true, 'deep', true);
        action(() => {
            this.checkedFolderIds = [];
        })();
        this.applyFileTreeFilter();
    };

    getSelected = (): TreeNode => {
        return this.tree?.getById(Number(this.selectedFolderId)) ?? null;
    };

    getTreeNodeById = (id: number): TreeNode => {
        return this.tree?.getById(id);
    };

    applyFileTreeFilter = _.debounce((): void => {
        const folderIds = this.tree?.getChecked().map(f => f.id) ?? [];
        this.props.store.setFilter({ folderIds });
    }, 100);

    openThumbnailsPanel(folderId: number, focusId?: number): void {
        this.props.store.showThumbnailsForFolderId = folderId;
        if (focusId) {
            this.props.store.focusThumbnailId = focusId;
        }
        action(() => {
            if (!this.props.mobileView) this.activeTab = 1;
            this.selectedFolderId = folderId;
        })();
    }

    showInPanel(idOrUuid: number | string): void {
        const image = typeof idOrUuid === 'number' ? this.props.store.getById(idOrUuid) : this.props.store.getByUuid(idOrUuid);
        if (image) {
            this.openThumbnailsPanel(image.folderId, image.id);
        }
    }

    closeThumbnailsPanel = (): void => {
        this.props.store.showThumbnailsForFolderId = null;
        this.props.store.focusThumbnailId = null;
        action(() => {
            this.activeTab = 0;
            this.selectedFolderId = null;
        })();
    };

    stateful = {
        queuedRestoreState: null,

        defaultState: {
            checkedFolderIds: [],
            uncheckedFolderIds: [],
        },

        restoreDefaultState: (): void => {
            this.props.store.clearFilter();
            this.selectAll();
            this.stateful.restoreState(this.stateful.defaultState);
        },

        getCurrentState: (): IMapPageStateDocumentsImagesPanel => {
            if (!this.tree) return null;

            const { checkedIds, uncheckedIds } = this.tree?.getCheckedAndUncheckedFolderIds();

            return {
                checkedFolderIds: checkedIds,
                uncheckedFolderIds: uncheckedIds,
                ...this.props.store.stateful.getCurrentState(),
            };
        },

        restoreState: (state: IMapPageStateDocumentsImagesPanel): void => {
            if (!state) return null;

            if (!this.tree) {
                this.stateful.queuedRestoreState = JSON.parse(JSON.stringify(state)) as IMapPageStateDocumentsImagesPanel;
                return;
            }

            this.props.store.stateful.restoreState(state);

            if (PageStateUtils.stateHasKey<IMapPageStateDocumentsImagesPanel>(state, 'checkedFolderIds') && Array.isArray(state.checkedFolderIds)) {
                const allFolderIds = new Set<number>();
                this.tree.each(
                    c => {
                        if (c.isFolder()) {
                            allFolderIds.add(c.id);
                        }
                    },
                    true,
                    true
                );

                state.checkedFolderIds = state.checkedFolderIds.filter(id => allFolderIds.has(id));
                this.props.store.filter.folderIds = state.checkedFolderIds;
                this.tree?.checkIds(state.checkedFolderIds, true, false);
            }

            this.stateful.queuedRestoreState = null;

            setTimeout(() => {
                this.applyFileTreeFilter();
            }, 50);
        },
    };
}
